import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Link as MuiLink,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { getTripLoadFactor } from "../../api/loadFactor";
import { isPlatformAdminUser, isTenantAdminUser } from "../../utils/auth";

const occupancyColor = (pct) => {
  if (pct >= 80) return "error";
  if (pct >= 50) return "warning";
  return "success";
};

const LoadFactorPage = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [dateFrom, setDateFrom] = useState(dayjs().format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(
    dayjs().add(7, "day").format("YYYY-MM-DD")
  );
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isPlatformAdminUser() && !isTenantAdminUser()) {
      navigate("/403", { replace: true });
    }
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getTripLoadFactor({
        date_from: dateFrom,
        date_to: dateTo,
      });
      setRows(data.results || []);
    } catch (e) {
      setError(t("admin.loadFactorLoadError"));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
      <MuiLink
        component={RouterLink}
        to="/administrator/dashboard"
        underline="hover"
        variant="body2"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 1 }}
      >
        <ArrowBackIcon fontSize="inherit" />
        {t("header.adminPanel")}
      </MuiLink>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {t("admin.loadFactorTitle")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("admin.loadFactorHint")}
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{ mb: 3 }}
      >
        <TextField
          type="date"
          size="small"
          label={t("admin.loadFactorFrom")}
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="date"
          size="small"
          label={t("admin.loadFactorTo")}
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="outlined" onClick={load} disabled={loading}>
          {t("admin.loadFactorRefresh")}
        </Button>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">{t("admin.loadFactorEmpty")}</Typography>
      ) : (
        <Paper variant="outlined" sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("admin.loadFactorRoute")}</TableCell>
                <TableCell>{t("admin.loadFactorDeparture")}</TableCell>
                <TableCell align="right">{t("admin.loadFactorSold")}</TableCell>
                <TableCell align="right">{t("admin.loadFactorCapacity")}</TableCell>
                <TableCell>{t("admin.loadFactorOccupancy")}</TableCell>
                <TableCell>{t("admin.loadFactorBoarding")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.trip_id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{row.route_name}</span>
                      {row.sold_out ? (
                        <Chip size="small" label={t("admin.tripSoldOut")} variant="outlined" />
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {row.departure_date
                      ? dayjs(row.departure_date).format("DD.MM.YYYY HH:mm")
                      : "—"}
                  </TableCell>
                  <TableCell align="right">
                    {row.sold} / {row.free_seats} {t("admin.loadFactorFree")}
                  </TableCell>
                  <TableCell align="right">{row.capacity}</TableCell>
                  <TableCell sx={{ minWidth: 140 }}>
                    <Stack spacing={0.5}>
                      <Chip
                        size="small"
                        label={`${row.occupancy_pct}%`}
                        color={occupancyColor(row.occupancy_pct)}
                        sx={{ width: "fit-content" }}
                      />
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, row.occupancy_pct || 0)}
                        color={occupancyColor(row.occupancy_pct)}
                      />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {row.boarded}/{row.passenger_count}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      component={RouterLink}
                      to={`/administrator/trips/${row.trip_id}/passengers`}
                    >
                      {t("admin.loadFactorPassengers")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Container>
  );
};

export default LoadFactorPage;
